/**
 * localStorage preferences cache.
 *
 * The pre-bootstrap inline script in ``ui/index.html`` reads
 * ``meridian.<network>.preferences.<user_id>`` at first paint and
 * applies the saved theme to ``<html data-theme="…">`` so the user
 * never sees a flash-of-default-theme (REQ-UI-044). This module
 * is the *writer* side: after the user saves preferences via the
 * gear-menu modal, we mirror the BFF's response back to
 * localStorage so the next cold load reads the up-to-date value.
 *
 * The cache is **not** the source of truth — the BFF's
 * ``auth.user_preferences`` row is. The cache exists exclusively
 * to bridge the pre-React paint gap. The :class:`SessionProvider`
 * reconciles the cache against the bootstrap response on every
 * mount so a stale cache self-corrects within one frame.
 *
 * **Schema v3 (ADR-033 §3.2).** The cached document now nests the
 * paint-relevant slices under ``working_state`` (``theme`` +
 * ``layout``), mirroring the preferences-v3 wire shape so the
 * inline pre-bootstrap script reads from one place
 * (``parsed.working_state.theme`` / ``parsed.working_state.layout``).
 * The layout slice is the Profile document's :type:`LayoutSettings`
 * (``dockview_state`` + ``preset_name``) — the multi-pane workspace
 * consumes it as the higher-priority initial layout.
 *
 * Cross-references:
 *   - REQ-UI-044 (pre-bootstrap script — applied before first
 *     paint to avoid flash).
 *   - REQ-AUTH-053 (BFF-side store is the source of truth).
 *   - REQ-AUTH-070 (preferences v3 — working_state).
 *   - ADR-022 Implementation Notes — localStorage cache rationale.
 *   - ADR-033 §3.2 (v3 working_state).
 *   - ADR-005 / ADR-009 (per-network isolation — the cache key
 *     embeds the network so a user with sessions on multiple
 *     networks doesn't cross-contaminate themes).
 */
import type { ThemeMode } from "@/auth/session-types";

/**
 * The paint-relevant slice of the working state we mirror to
 * localStorage: the ``theme`` (drives ``<html data-theme>`` before
 * paint) and the opaque ``layout`` sub-document (the multi-pane
 * workspace's higher-priority initial layout per REQ-UI-053). The
 * full working state is **not** cached — only what the pre-paint
 * script and the workspace cold-mount need.
 */
export interface CachedWorkingState {
  theme: ThemeMode;
  /** The Profile document's ``layout`` sub-document — opaque here
   *  (typed strictly by the multi-pane consumer). */
  layout?: unknown;
}

/**
 * The shape we serialise to localStorage — preferences **schema
 * version 3** (ADR-033 §3.2). ``working_state`` carries the
 * paint-relevant slices; ``cached_at`` is a wall-clock hook for
 * future stale-cache detection (no TTL is enforced today —
 * REQ-UI-044's bootstrap-reconciliation is sufficient for MVP).
 */
export interface CachedPreferences {
  schema_version: 3;
  working_state: CachedWorkingState;
  cached_at: string; // ISO 8601
}

/**
 * Build the cache key per Flow P (``localStorage cache`` section):
 * ``meridian.<network>.preferences.<user_id>``. The pre-bootstrap
 * script in ``index.html`` scans for ``meridian.*.preferences.*``
 * keys and picks the lexicographically last one — single-user
 * sessions converge on one entry.
 */
export function makePreferencesCacheKey(network: string, userId: string): string {
  return `meridian.${network}.preferences.${userId}`;
}

/**
 * Persist the supplied preferences document to localStorage.
 * Silently no-ops when ``localStorage`` is unavailable (private
 * browsing, storage quota exhausted, server-side render).
 */
export function writePreferencesCache(
  network: string,
  userId: string,
  value: Omit<CachedPreferences, "cached_at">,
): void {
  if (typeof window === "undefined" || typeof window.localStorage === "undefined") {
    return;
  }
  try {
    const payload: CachedPreferences = { ...value, cached_at: new Date().toISOString() };
    window.localStorage.setItem(makePreferencesCacheKey(network, userId), JSON.stringify(payload));
  } catch {
    // Out of quota or storage disabled. The cache is an optimisation;
    // the post-mount reconcile + next-cold-load still work, the
    // user just sees a one-frame flash. Acceptable per Flow P
    // edge case "Browser localStorage disabled / blocked".
  }
}

/**
 * Read the cached preferences document. Returns ``null`` when the
 * key is missing or the value can't be parsed (we deliberately
 * mirror the inline script's defensive posture — a corrupted
 * cache must never block boot).
 */
export function readPreferencesCache(network: string, userId: string): CachedPreferences | null {
  if (typeof window === "undefined" || typeof window.localStorage === "undefined") {
    return null;
  }
  try {
    const raw = window.localStorage.getItem(makePreferencesCacheKey(network, userId));
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as unknown;
    // v3 shape: the theme lives under ``working_state.theme``. A
    // corrupted or pre-v3 entry (no ``working_state.theme``) is
    // treated as a miss so the inline script + post-mount reconcile
    // fall back to the dark default rather than honouring a stale
    // v2 shape.
    if (
      typeof parsed === "object" &&
      parsed !== null &&
      "working_state" in parsed &&
      typeof (parsed as { working_state: unknown }).working_state === "object" &&
      (parsed as { working_state: { theme?: unknown } }).working_state !== null &&
      (parsed as { working_state: { theme?: unknown } }).working_state.theme !== undefined
    ) {
      return parsed as CachedPreferences;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Drop the cache entry — used by the "Reset to defaults" flow so
 * a subsequent cold load sees no entry and applies the inline
 * script's fallback (``system`` theme).
 */
export function clearPreferencesCache(network: string, userId: string): void {
  if (typeof window === "undefined" || typeof window.localStorage === "undefined") {
    return;
  }
  try {
    window.localStorage.removeItem(makePreferencesCacheKey(network, userId));
  } catch {
    // Same posture as writePreferencesCache — best-effort.
  }
}
